import { useState } from "react";
import Modal from "./Modal";

const API_BASE = import.meta.env.VITE_API_BASE as string;

interface Props {
  open: boolean;
  onClose: () => void;
  loId: string;
  loName: string;
  webAppId: string;
  agentSlug: string | null;
}

type Status = "idle" | "sending" | "sent" | "error";

/**
 * Shown when the LO is offline and the visitor clicks "Send a message".
 * Collects name, email, and the message; posts to /mlo/messages; lands
 * in the MLO's inbox in the admin.
 */
export default function MessageModal({ open, onClose, loId, loName, webAppId, agentSlug }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  if (!open) return null;

  const canSubmit =
    status !== "sending" &&
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    text.trim().length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    setErrorMessage("");
    try {
      const res = await fetch(`${API_BASE}/mlo/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lo_id: loId,
          web_app_id: webAppId,
          agent_slug: agentSlug,
          name: name.trim(),
          email: email.trim(),
          message: text.trim(),
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        setErrorMessage(`We couldn't send your message: ${t || res.statusText}`);
        setStatus("error");
        return;
      }
      setStatus("sent");
    } catch (err) {
      setErrorMessage("Network error. Please try again in a moment.");
      setStatus("error");
    }
  }

  function reset() {
    setName(""); setEmail(""); setText("");
    setStatus("idle"); setErrorMessage("");
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={status === "sent" ? "Message sent" : `Send ${loName.split(" ")[0]} a message`}
      subtitle={status === "sent"
        ? "They'll get back to you as soon as they're available."
        : `${loName.split(" ")[0]} isn't available for a live call right now. Leave a message and they'll follow up.`}
      size="md"
    >
      {status === "sent" ? (
        <div className="msg-form__done">
          <div className="msg-form__done-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
                 stroke="currentColor" strokeWidth="2.4"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <div className="msg-form__done-title">Message sent</div>
          <p className="msg-form__done-sub">
            Thanks — your note is on its way to {loName.split(" ")[0]}.
            You'll hear back at the email you provided.
          </p>
          <button
            className="btn btn--primary btn--lg"
            onClick={() => { reset(); onClose(); }}
          >
            Close
          </button>
        </div>
      ) : (
        <form className="msg-form" onSubmit={submit} noValidate>
          <div className="msg-form__intro" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>Typically replies within one business day.</span>
          </div>

          <div className="msg-form__row">
            <div className="msg-form__field">
              <label className="msg-form__label" htmlFor="msg-name">Your name</label>
              <input
                id="msg-name"
                className="msg-form__input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="Jane Doe"
                required
                maxLength={200}
              />
            </div>
            <div className="msg-form__field">
              <label className="msg-form__label" htmlFor="msg-email">Email</label>
              <input
                id="msg-email"
                className="msg-form__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="jane@example.com"
                required
                maxLength={200}
              />
            </div>
          </div>

          <div className="msg-form__field">
            <div className="msg-form__label-row">
              <label className="msg-form__label" htmlFor="msg-body">Message</label>
              <span
                className={`msg-form__count ${text.length > 3800 ? "is-warn" : ""}`}
                aria-live="polite"
              >
                {text.length}/4000
              </span>
            </div>
            <textarea
              id="msg-body"
              className="msg-form__textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
              maxLength={4000}
              rows={5}
              placeholder="Hi, I have a question about VA loan eligibility…"
            />
          </div>

          {errorMessage && (
            <div className="msg-form__error" role="alert">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
              </svg>
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="msg-form__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => { reset(); onClose(); }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary btn--lg msg-form__submit"
              disabled={!canSubmit}
            >
              {status === "sending" ? (
                <>
                  <span className="msg-form__spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m22 2-7 20-4-9-9-4 20-7z" />
                  </svg>
                  Send message
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
